import { useMemo } from 'react'
import dayjs, { Dayjs } from 'dayjs'
import { getUnifiedMonthlyPrice, idKey } from '../helpers'
import { ClassRecord, GenerateClassRecordTemplate } from '../types'

type DateGroupMeta = {
  rowSpanMap: Record<string, number>
  groupIndexMap: Record<string, number>
}

export function useClassRecordTableMeta(
  records: ClassRecord[],
  selectedMonth: Dayjs | null,
) {
  const rangePresets = useMemo(() => {
    if (!selectedMonth) {
      return []
    }
    const monthStart = selectedMonth.startOf('month')
    const monthEnd = selectedMonth.endOf('month')
    const firstHalfEnd = selectedMonth.date(15)
    const secondHalfStart = selectedMonth.date(16)

    return [
      { label: '整月', value: [monthStart, monthEnd] as [Dayjs, Dayjs] },
      { label: '上半月', value: [monthStart, firstHalfEnd] as [Dayjs, Dayjs] },
      { label: '下半月', value: [secondHalfStart, monthEnd] as [Dayjs, Dayjs] },
    ]
  }, [selectedMonth])

  const dailySequenceMap = useMemo(() => {
    const counter: Record<string, number> = {}
    const sequence: Record<string, number> = {}

    records.forEach((record) => {
      const dateKey = dayjs(record.classDate).format('YYYY-MM-DD')
      counter[dateKey] = (counter[dateKey] ?? 0) + 1
      sequence[idKey(record.id)] = counter[dateKey]
    })

    return sequence
  }, [records])

  const dateGroupMeta = useMemo<DateGroupMeta>(() => {
    const rowSpanMap: Record<string, number> = {}
    const groupIndexMap: Record<string, number> = {}

    let index = 0
    let groupIndex = 0

    while (index < records.length) {
      const currentDate = dayjs(records[index].classDate).format('YYYY-MM-DD')
      let end = index

      while (
        end < records.length &&
        dayjs(records[end].classDate).format('YYYY-MM-DD') === currentDate
      ) {
        end += 1
      }

      const count = end - index
      for (let i = index; i < end; i += 1) {
        const key = idKey(records[i].id)
        groupIndexMap[key] = groupIndex
        rowSpanMap[key] = i === index ? count : 0
      }

      groupIndex += 1
      index = end
    }

    return {
      rowSpanMap,
      groupIndexMap,
    }
  }, [records])

  const unifiedMonthlyPriceByDate = useMemo(() => {
    const groupedTemplates = new Map<string, GenerateClassRecordTemplate[]>()

    records.forEach((record) => {
      const dateKey = dayjs(record.classDate).format('YYYY-MM-DD')
      const current = groupedTemplates.get(dateKey) ?? []
      current.push({
        classType: record.classType,
        billingMode: record.billingMode,
        teacherId: record.teacherId ?? undefined,
        teacherName: record.teacherName ?? null,
        coursePrice: record.coursePrice ?? undefined,
      })
      groupedTemplates.set(dateKey, current)
    })

    const result: Record<string, number | null> = {}
    groupedTemplates.forEach((templates, dateKey) => {
      result[dateKey] = getUnifiedMonthlyPrice(templates)
    })

    return result
  }, [records])

  return {
    rangePresets,
    dailySequenceMap,
    dateGroupMeta,
    unifiedMonthlyPriceByDate,
  }
}
